import React, { useEffect, useMemo, useState } from 'react';
import { useEntities, fetcher } from '../../../hooks/useSWR';
import GenericFormsPage from '../../../components/generic/GenericFormsPage';
import { initializeModule } from '../../../utils/configInit';
import { myManualsConfig } from './myManualsConfig';
import { createMyManualsValidationSchema } from './myManualsValidation';
import { mapManualAssignmentToEntity } from './mapManualAssignment';
import { MANUAL_ENTITY_TYPE_ID } from '../manualConfig';

const { config: baseConfig } = initializeModule(
  myManualsConfig,
  createMyManualsValidationSchema
);

const MyManualsModulePage = () => {
  const { data: assignments = [], error, isLoading } = useEntities(
    baseConfig.api.baseEndpoint
  );

  const [enrichedEntities, setEnrichedEntities] = useState([]);

  const mappedEntities = useMemo(
    () =>
      Array.isArray(assignments) ? assignments.map(mapManualAssignmentToEntity) : [],
    [assignments]
  );

  useEffect(() => {
    let cancelled = false;

    const loadFileCounts = async () => {
      if (!mappedEntities.length) {
        setEnrichedEntities([]);
        return;
      }

      const rows = await Promise.all(
        mappedEntities.map(async (row) => {
          if (!row.canView || !row.manualId) {
            return row;
          }
          try {
            const files = await fetcher(
              `/file-attachment/entity/${MANUAL_ENTITY_TYPE_ID}/${row.manualId}`
            );
            const active = (files || []).filter((f) => !f.isDeleted);
            return {
              ...row,
              fileAttachments: active,
              fileAttachmentCount: active.length,
            };
          } catch {
            return row;
          }
        })
      );

      if (!cancelled) {
        setEnrichedEntities(rows);
      }
    };

    loadFileCounts();
    return () => {
      cancelled = true;
    };
  }, [mappedEntities]);

  const displayData = enrichedEntities.length ? enrichedEntities : mappedEntities;

  const configWithData = useMemo(
    () => ({
      ...baseConfig,
      data: displayData,
      onSave: async () => {
        throw new Error('My Manuals is read-only.');
      },
      onDelete: async () => {
        throw new Error('My Manuals is read-only.');
      },
    }),
    [displayData]
  );

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Error loading My Manuals: {error.message}
        </div>
      </div>
    );
  }

  if (isLoading && !displayData.length) {
    return (
      <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading My Manuals…</div>
    );
  }

  return <GenericFormsPage config={configWithData} />;
};

export default MyManualsModulePage;
